const REF_COOKIE = "progsity_ref";
/** The admin default in `ReferralSettings.cookieDurationDays`; used when the public settings read failed. */
export const DEFAULT_REFERRAL_COOKIE_DAYS = 30;
const MAX_REFERRAL_COOKIE_DAYS = 400;
const SECONDS_PER_DAY = 86_400;

/**
 * The cookie lifetime in seconds for an admin-set day count (`docs/19` §4).
 * Until 2026-09-04 this was hard-coded to 30 days and the admin setting was
 * ignored. Anything that is not a positive number falls back to the default,
 * and 400 days is the ceiling browsers apply to a cookie anyway.
 */
export function referralCookieMaxAgeSeconds(days: number | undefined): number {
  const clamped =
    typeof days === "number" && Number.isFinite(days) && days >= 1
      ? Math.min(Math.floor(days), MAX_REFERRAL_COOKIE_DAYS)
      : DEFAULT_REFERRAL_COOKIE_DAYS;
  return clamped * SECONDS_PER_DAY;
}

/**
 * Persists referral code from `?ref=` for checkout (client-side; readable by JS).
 */
export function setReferralCookie(code: string, durationDays?: number): void {
  if (typeof document === "undefined") return;
  const trimmed = code.trim();
  if (!trimmed) return;
  const maxAge = referralCookieMaxAgeSeconds(durationDays);
  document.cookie = `${REF_COOKIE}=${encodeURIComponent(trimmed)}; max-age=${maxAge}; path=/; samesite=lax`;
}

export function getReferralCode(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${REF_COOKIE}=([^;]*)`),
  );
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * `GR-SHR01`: an address someone shares, carrying their referral code as
 * `?ref=`, the parameter `ReferralTracker` turns into the cookie above on any
 * localized route. No code (signed out, or none issued) leaves the address as
 * it was. Only the code goes in: never an email or an account id.
 */
export function withReferralParam(url: string, code: string | null | undefined): string {
  const ref = code?.trim();
  if (!ref) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("ref", ref);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function clearReferralCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REF_COOKIE}=; max-age=0; path=/; samesite=lax`;
}
